import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { TAILORING_ENTRIES, tailoringSlug, tailoringDisplayName, type TailoringEntry } from './Tailoring';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&h=800&fit=crop';

function mapsUrl(e: TailoringEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export default function TailoringDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('tailoring_listings', TAILORING_ENTRIES, (e, i) =>
    slugify(e.name || String(i))
  );

  const entry: TailoringEntry | undefined = mc.items.find(
    (e: any) => tailoringSlug(e.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Lora', serif" }}
      >
        <SEO
          title="Tailor Not Found | Dallas Desi Scoop"
          description="This tailoring listing could not be found."
          canonical="/tailoring"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Tailor not found.</p>
        <Link
          to="/tailoring"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Tailoring
        </Link>
      </div>
    );
  }

  const displayName = tailoringDisplayName(entry.name);
  // Individual detail pages get their own longer SEO description when set
  // (via `detailDescription`), falling back to the short card blurb.
  const longDescription = entry.detailDescription || entry.description;
  const city = entry.address?.split(',').slice(-2, -1)[0]?.trim() || entry.address || 'DFW';
  const title = `${displayName} in ${city} | Tailoring & Alterations | Dallas Desi Scoop`;
  const description =
    entry.detailDescription ||
    `${displayName}${entry.description ? ` offers ${entry.description}` : ' offers tailoring and alteration services'} in ${city}, TX. Find address, phone number, and contact info.`;
  const canonicalPath = `/tailoring/${slug}`;

  // Split the stored address into a street portion and a locality (city) so the
  // PostalAddress carries a real addressLocality field, not just one combined string.
  // Supported shapes: "Street, City, TX Zip" (3 parts), "City, TX" (2 parts), "City" (1 part).
  const addressParts = (entry.address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      name: displayName,
      image: entry.image || FALLBACK_IMAGE,
      description: longDescription || 'Tailoring and alteration services',
      address: {
        '@type': 'PostalAddress',
        streetAddress: streetAddress,
        addressLocality: addressLocality || undefined,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.phone || undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Tailoring', item: `${BASE_URL}/tailoring` },
        { '@type': 'ListItem', position: 3, name: displayName, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Lora', serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/tailoring"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Tailoring
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{displayName}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {displayName}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            {entry.description && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">{entry.description}</span>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {displayName}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.detailDescription
              ? entry.detailDescription
              : `${displayName} provides ${entry.description ? entry.description.toLowerCase() : 'tailoring and alteration services'} for the South Asian community across Dallas–Fort Worth.${entry.address ? ` Located at ${entry.address}.` : ''}`}
          </p>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              <span>{entry.address || '[Coming soon]'}</span>
            </div>
            {entry.phone && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.phone.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.phone}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          {entry.facebook && (
            <div className="mt-5">
              <SocialLinks item={{ facebook: entry.facebook }} variant="bar" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
